/**
 * Foundation · Timesheet — griglia mensile in stile "foglio presenze" (riusabile da tutta la GI Suite).
 * In primo piano: Giorno · Entrata · Uscita · Entrata · Uscita · Ore. Controlli e giustificativi discreti.
 * Tastiera: Invio = campo successivo · ↑/↓ = riga · F9 = copia giorno precedente ·
 * Shift+F9 = copia settimana precedente · Shift+Canc = svuota giornata · Alt+C = "Copia da…".
 * Incolla da Excel: celle separate da TAB, righe da a capo, a partire dalla cella attiva.
 */
import React, { memo, useCallback, useRef } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { formatDuration, useDurationFormat } from "@/foundation/duration";
import { normalizeTimeInput, parseTime, parseClipboardGrid } from "./timeCore";
import { JustificationCell } from "./JustificationCell";
import { DayCheckCell } from "./DayCheckCell";

const WD = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const ORD = ["Prima", "Seconda", "Terza", "Quarta"];

const TimeInput = ({ value, row, col, readOnly, invalid, onChange, onPasteGrid, testid }) => (
  <input
    type="text" inputMode="numeric" autoComplete="off" value={value || ""} readOnly={readOnly}
    data-ts-row={row} data-ts-col={col} data-testid={testid} placeholder="__:__" aria-invalid={invalid || undefined}
    onChange={(e) => onChange(e.target.value.replace(/[^\d:.,]/g, "").slice(0, 5))}
    onBlur={(e) => { const n = normalizeTimeInput(e.target.value); if (n !== e.target.value) onChange(n); }}
    onPaste={(e) => {
      const text = e.clipboardData.getData("text");
      if (/[\t\n]/.test(text.trim()) && onPasteGrid) { e.preventDefault(); onPasteGrid(row, col, parseClipboardGrid(text)); }
    }}
    className={`h-8 w-[68px] rounded-sm border-0 border-b bg-transparent text-center font-mono text-[15px] tracking-wide text-suite-neutral-900 placeholder:text-suite-neutral-300
      transition-colors focus:bg-white focus:outline-none focus:ring-2 focus:ring-suite-primary/40
      ${invalid ? "border-suite-error bg-red-50 text-suite-error" : "border-suite-neutral-300"} ${readOnly ? "cursor-default" : ""}`}
  />
);

const Row = memo(function Row({ idx, day, meta, ev, intervalCount, readOnly, options, onDay, onPasteGrid, fmt }) {
  const d = new Date(`${day.date}T00:00:00`);
  const wd = (d.getDay() + 6) % 7;
  const holiday = meta?.holiday;
  const rest = wd >= 5 || !!holiday;
  const theo = meta?.expectedMinutes ?? null;
  const setIv = (i, key, v) => onDay(day.date, { ...day, intervals: day.intervals.map((iv, k) => (k === i ? { ...iv, [key]: v } : iv)) });
  const badIv = new Set((ev?.issues || []).filter((x) => x.level === "error" && x.interval !== undefined).map((x) => x.interval));
  const remaining = theo === null ? 0 : theo - (ev?.workedMinutes || 0) - (ev?.justifiedMinutes || 0);
  const suggest = theo > 0 && (ev?.workedMinutes || 0) > 0 && remaining > 0;
  const bad = (i, k) => badIv.has(i) || (day.intervals[i]?.[k] && parseTime(day.intervals[i][k]) === null);
  return (
    <tr className={`group border-b border-dashed border-suite-neutral-200 transition-colors focus-within:bg-suite-primary-light/50 hover:bg-suite-primary-light/30 ${holiday ? "bg-amber-50/50" : rest ? "bg-suite-neutral-50/80" : "bg-white"}`}
      data-testid={`ts-row-${day.date}`}>
      <td className="sticky left-0 z-10 bg-inherit py-1 pl-3 pr-2 whitespace-nowrap">
        <span className={`inline-block w-6 font-mono text-[15px] font-semibold ${rest ? "text-suite-error" : "text-suite-neutral-900"}`}>{String(d.getDate()).padStart(2, "0")}</span>
        <span className={`text-[11px] ${rest ? "text-suite-error" : "text-suite-neutral-500"}`}>{WD[wd]}</span>
        {holiday && <div className="max-w-[110px] truncate text-[10px] text-amber-700" title={holiday.name}>{holiday.name}</div>}
        {meta?.cartellinoHasData && <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-sky-500 align-middle" title="Dati già presenti nel Cartellino" data-testid={`ts-row-${day.date}-has-cartellino`} />}
      </td>
      {Array.from({ length: intervalCount }).flatMap((_, i) => [
        <td key={`s${i}`} className={`px-1.5 py-1 ${i > 0 ? "border-l border-suite-neutral-100" : ""}`}>
          <TimeInput value={day.intervals[i]?.start} row={idx} col={i * 2} readOnly={readOnly} invalid={bad(i, "start")}
            onChange={(v) => setIv(i, "start", v)} onPasteGrid={onPasteGrid} testid={`ts-${day.date}-in-${i + 1}`} />
        </td>,
        <td key={`e${i}`} className="px-1.5 py-1">
          <TimeInput value={day.intervals[i]?.end} row={idx} col={i * 2 + 1} readOnly={readOnly} invalid={bad(i, "end")}
            onChange={(v) => setIv(i, "end", v)} onPasteGrid={onPasteGrid} testid={`ts-${day.date}-out-${i + 1}`} />
        </td>,
      ])}
      <td className="border-l border-suite-neutral-200 px-3 text-right font-mono text-[15px] font-semibold text-suite-neutral-900" data-testid={`ts-${day.date}-worked`}>
        {ev?.workedMinutes ? formatDuration(ev.workedMinutes / 60, fmt) : ""}
      </td>
      <td className="px-1 min-w-[110px]">
        <JustificationCell day={day} options={options} remainingMinutes={remaining} readOnly={readOnly} suggest={suggest}
          onChange={(list) => onDay(day.date, { ...day, justifications: list })} testid={`ts-${day.date}-just`} />
      </td>
      <td className="px-2"><DayCheckCell date={day.date} ev={ev} theo={theo} fmt={fmt} /></td>
    </tr>
  );
});

export function TimesheetGrid({ days, meta, evaluations, intervalCount = 2, readOnly, justificationOptions, onDayChange, onCopyFrom, onClearDay, onPasteGrid, onActiveRow, onOpenCopy }) {
  const fmt = useDurationFormat();
  const ref = useRef(null);
  const focusCell = useCallback((row, col) => {
    const el = ref.current?.querySelector(`[data-ts-row="${row}"][data-ts-col="${col}"]`);
    if (el) { el.focus(); el.select?.(); }
  }, []);

  const onKeyDown = (e) => {
    const t = e.target;
    if (e.altKey && (e.key === "c" || e.key === "C") && onOpenCopy) { e.preventDefault(); onOpenCopy(); return; }
    if (!t.dataset?.tsRow) return;
    const row = Number(t.dataset.tsRow);
    const col = Number(t.dataset.tsCol);
    const last = intervalCount * 2 - 1;
    if (e.key === "Enter") { e.preventDefault(); t.blur(); col < last ? focusCell(row, col + 1) : focusCell(row + 1, 0); }
    else if (e.key === "ArrowDown") { e.preventDefault(); focusCell(row + 1, col); }
    else if (e.key === "ArrowUp") { e.preventDefault(); focusCell(row - 1, col); }
    else if (e.key === "F9" && !readOnly) { e.preventDefault(); onCopyFrom?.(row, e.shiftKey ? 7 : 1); }
    else if (e.key === "Delete" && e.shiftKey && !readOnly) { e.preventDefault(); onClearDay?.(row); }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div ref={ref} onKeyDown={onKeyDown} onFocus={(e) => e.target.dataset?.tsRow && onActiveRow?.(Number(e.target.dataset.tsRow))}
        className="max-h-[calc(100vh-400px)] min-h-[300px] overflow-auto rounded-lg border border-suite-neutral-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)]" data-testid="timesheet-grid">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-20 bg-white">
            <tr className="text-[10px] uppercase tracking-[0.14em] text-suite-neutral-400">
              <th className="sticky left-0 z-30 bg-white" />
              {Array.from({ length: intervalCount }).map((_, i) => <th key={i} colSpan={2} className={`pt-2 font-medium ${i > 0 ? "border-l border-suite-neutral-100" : ""}`}>{ORD[i] || `${i + 1}ª`} fascia</th>)}
              <th colSpan={3} />
            </tr>
            <tr className="border-b-2 border-suite-neutral-800 text-[11px] font-semibold uppercase tracking-wider text-suite-neutral-700">
              <th className="sticky left-0 z-30 bg-white py-2 pl-3 text-left">Giorno</th>
              {Array.from({ length: intervalCount }).flatMap((_, i) => [
                <th key={`s${i}`} className={`px-1.5 py-2 ${i > 0 ? "border-l border-suite-neutral-100" : ""}`}>Entrata</th>,
                <th key={`e${i}`} className="px-1.5 py-2">Uscita</th>,
              ])}
              <th className="border-l border-suite-neutral-200 px-3 py-2 text-right">Ore</th>
              <th className="px-2 py-2 text-left text-[10px] font-medium text-suite-neutral-400">Giustificativo</th>
              <th className="px-2 py-2 text-left text-[10px] font-medium text-suite-neutral-400">Controllo</th>
            </tr>
          </thead>
          <tbody>
            {days.map((day, idx) => (
              <Row key={day.date} idx={idx} day={day} meta={meta[day.date]} ev={evaluations[day.date]} intervalCount={intervalCount}
                readOnly={readOnly} options={justificationOptions} onDay={onDayChange} onPasteGrid={onPasteGrid} fmt={fmt} />
            ))}
          </tbody>
        </table>
      </div>
    </TooltipProvider>
  );
}

export default TimesheetGrid;
